/** Minimal shape needed to order sections into their display tree. */
export interface TreeSection {
  sectionId: string;
  parentSectionId: string | null;
  sortOrder: number;
}

/**
 * Flatten sections into the SAME order the list renders them — a pre-order
 * depth-first walk with each level sorted by `sortOrder` — and annotate each
 * with its nesting `depth` (0 = top-level). A section whose parent isn't in the
 * list is treated as a root, so nothing is ever dropped.
 *
 * Used to render an indented, tree-ordered Section picker in the item editor.
 */
export function flattenSectionTree<T extends TreeSection>(
  sections: T[],
): Array<{ section: T; depth: number }> {
  const ids = new Set(sections.map((s) => s.sectionId));
  const byParent = new Map<string | null, T[]>();
  for (const s of sections) {
    // Missing/unknown parent → treat as a root.
    const parent = s.parentSectionId && ids.has(s.parentSectionId) ? s.parentSectionId : null;
    const arr = byParent.get(parent) ?? [];
    arr.push(s);
    byParent.set(parent, arr);
  }
  for (const arr of byParent.values()) arr.sort((a, b) => a.sortOrder - b.sortOrder);

  const out: Array<{ section: T; depth: number }> = [];
  const walk = (parentId: string | null, depth: number): void => {
    for (const s of byParent.get(parentId) ?? []) {
      out.push({ section: s, depth });
      walk(s.sectionId, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}
